import { useOnClickOutside } from '@easeltv/hooks';
import { camelToProperCase } from '@easeltv/utilities';
import {
    KeyboardEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    ErrorText,
    Label,
    LabelWrapper,
    RequiredSpan,
    TextInput,
} from '../TextFieldAtom/TextFieldAtom.styled';
import {
    Button,
    Icon,
    InputWrapper,
    List,
    StyledDropdownLabel,
} from './DropdownAtom.styled';

export interface DumbDropdownAtomI {
    /** Indicates if an error exists for this field */
    hasError: boolean;

    /** Boolean as to whether the dropdown includes the filter functionality */
    canFilter?: boolean;

    /** Display label for the field */
    label: string;

    /** Array of options to choose from */
    options?: {
        value: string;
        label: string;
    }[];

    /** What to show when nothing is selected */
    placeholder: string;

    /** Whether the field is required to continue */
    required: boolean;

    /** Callback to trigger to update the value managed by the form */
    updateField: (value: string) => void;

    /** The current value of the field, managed by the form */
    value: string;

    /** Error message to be displayed below each field */
    errorText?: string;
}

const DumbDropdownAtom = ({
    canFilter = false,
    hasError,
    label,
    options = [],
    placeholder,
    required,
    updateField,
    value,
    errorText,
}: DumbDropdownAtomI) => {
    // Value is not always a string, for filtering and comparisons we cast it to a string type
    const stringValue = String(value);

    const [open, setOpen] = useState(false);
    const [activeItem, setActiveItem] = useState(0);

    const [filter, setFilter] = useState(stringValue);
    const [touched, setTouched] = useState(false);

    // Return the filtered options to be mapped into options
    const filteredOptions = useMemo(() => {
        // If no filtering has happened yet, show everything
        if (!touched || filter.trim().length < 1) return options;

        return options.filter((option) =>
            option.label.toLowerCase().includes(filter.toLowerCase()),
        );
    }, [filter, options, touched]);

    // Reset dropdown to closed state
    const resetDropdown = () => {
        setActiveItem(0);
        setOpen(false);
        setTouched(false);
    };

    // Close on click outside
    const dropdownRef = useRef<HTMLLabelElement>(null);
    useOnClickOutside(dropdownRef, resetDropdown);

    // Set current selection as active item when opening dropdown
    // If none selected, select the first option
    const openDropdown = () => {
        setOpen(true);
        setActiveItem(() => {
            const index = filteredOptions.findIndex(
                (option) => option.value === stringValue,
            );
            return index > -1 ? index : 0;
        });
    };

    // When option is selected, update field value
    const handleOnClick = (item: string) => (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();

        updateField(item);
        setFilter(camelToProperCase(item));

        resetDropdown();
    };

    // Handle those key presses
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!open && e.key !== 'Tab') {
                setOpen(true);
            } else {
                switch (e.key) {
                    case 'Enter': {
                        e.preventDefault();
                        if (filteredOptions.length > 0) {
                            updateField(filteredOptions[activeItem].value);
                            setFilter(filteredOptions[activeItem].label);
                        } else {
                            setFilter(camelToProperCase(stringValue));
                        }
                        resetDropdown();
                        break;
                    }
                    case 'ArrowUp': {
                        e.preventDefault();
                        setActiveItem((prev) => (prev < 1 ? 0 : prev - 1));
                        break;
                    }
                    case 'ArrowDown': {
                        e.preventDefault();
                        setActiveItem((prev) =>
                            prev === filteredOptions.length - 1
                                ? filteredOptions.length - 1
                                : prev + 1,
                        );
                        break;
                    }
                    case 'Tab': {
                        resetDropdown();
                        break;
                    }
                    case 'Escape': {
                        resetDropdown();
                        break;
                    }
                    default:
                        break;
                }
            }
        },
        [filteredOptions, activeItem, open, updateField, stringValue],
    );

    // When using keys to navigate make sure active item is in view of scroll
    // Also make sure when opened the selected value is in view
    useLayoutEffect(
        () =>
            dropdownRef.current?.querySelector('.active')?.scrollIntoView({
                behavior: 'smooth',
                inline: 'nearest',
                block: 'nearest',
            }),
        [activeItem, open],
    );

    // Highlight the current item if possible when updating the filter
    useEffect(() => {
        setActiveItem(() => {
            const index = filteredOptions.findIndex(
                (option) => option.value === stringValue,
            );
            return index > -1 ? index : 0;
        });
    }, [filteredOptions, stringValue]);

    return (
        <StyledDropdownLabel
            onClick={openDropdown}
            onKeyDown={handleKeyDown}
            open={open}
            ref={dropdownRef}
            role="button"
            tabIndex={0}
            hasError={hasError}
        >
            <LabelWrapper>
                <Label>
                    {label} {required && <RequiredSpan>*</RequiredSpan>}
                </Label>
                {hasError && errorText && <ErrorText>{errorText}</ErrorText>}
            </LabelWrapper>
            <InputWrapper>
                <TextInput
                    tabIndex={-1}
                    value={open ? filter : camelToProperCase(stringValue)}
                    placeholder={placeholder}
                    disabled={!canFilter}
                    onChange={(e) => {
                        setFilter(e.currentTarget.value);
                        setTouched(true);
                    }}
                />
                <Icon className="fa fa-dropdown" />
            </InputWrapper>
            {open && filteredOptions.length > 1 && (
                <List>
                    {filteredOptions.map((option, index) => (
                        <Button
                            key={option.value}
                            active={index === activeItem}
                            className={index === activeItem ? 'active' : ''}
                            selected={option.value === stringValue}
                            onClick={handleOnClick(option.value)}
                        >
                            {option.label}
                        </Button>
                    ))}
                </List>
            )}
        </StyledDropdownLabel>
    );
};

export default DumbDropdownAtom;
